import {
  DetailRequirement,
  EngagementTaskTrait,
  MultipleTaskTraitDetail,
  SingularTaskTraitDetail,
} from '@cibo/core'
import { Markdown } from '@cibo/ui'
import ClearIcon from '@mui/icons-material/Clear'
import {
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material'
import { useDebounceCallback } from '@react-hook/debounce'
import { UseQueryResult } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useProgramEngagementContext } from '../../../../../context'
import { TaskDataModel } from '../../../models/TaskDataModel'

type EmailDataEntryFieldProps = {
  taskId: string
  traitConfig: EngagementTaskTrait
  taskData: UseQueryResult<TaskDataModel, Error>
  detailRequirement?: DetailRequirement
  resourceId: string
  onChange: (detail: MultipleTaskTraitDetail | SingularTaskTraitDetail) => void
  immutable?: boolean
  traitDetailValue: any
  saveError?: Error
  ciboSourced?: boolean
}

let EMAIL_REGX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const validator = Yup.string().matches(EMAIL_REGX)

export const EmailDataEntryField = ({
  detailRequirement,
  traitConfig,
  onChange,
  immutable,
  traitDetailValue,
  saveError,
  ciboSourced,
}: EmailDataEntryFieldProps) => {
  const { t } = useTranslation(['@cibo/programs/EmailDataEntryField', '@cibo/programs/DataEntry'])
  const { taskData, engagement } = useProgramEngagementContext()
  const [validationError, setValidationError] = useState<boolean | undefined>()

  const traitStatus = taskData.data?.getTraitStatus(traitConfig.traitId)
  const params = traitStatus?.params

  const helperText = params?.helperTextMd

  const [displayValue, setDisplayValue] = useState<string | null>()

  const ciboSourcedValue = ciboSourced && traitDetailValue

  useEffect(() => {
    if (!displayValue && !ciboSourced) setDisplayValue(traitDetailValue)
  }, [traitDetailValue])

  const saveNewValue = useDebounceCallback((newValue: string | null) => {
    onChange({
      traitId: traitConfig.traitId,
      value: newValue,
      year: detailRequirement?.year,
    })
  }, 300)

  useEffect(() => {
    if (displayValue !== undefined) {
      const valid = validator.isValidSync(displayValue)

      if (valid || displayValue === null) {
        setValidationError(undefined)
        saveNewValue(displayValue)
      } else {
        setValidationError(true)
      }
    }
  }, [displayValue])

  return (
    <Stack spacing={1}>
      <TextField
        disabled={immutable}
        error={!!saveError || validationError}
        onChange={event => setDisplayValue(event.target.value)}
        value={displayValue || ''}
        slotProps={{
          input: {
            type: 'email',
            endAdornment: (
              <Stack direction="row" alignItems="center">
                <InputAdornment position="end">
                  <Tooltip title={t('removeValue')}>
                    <IconButton
                      data-testid="remove-email-button"
                      onClick={() => setDisplayValue(null)}
                      disabled={immutable}
                    >
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              </Stack>
            ),
          },
        }}
      />

      <Collapse in={!!ciboSourcedValue}>
        <Button
          onClick={() => setDisplayValue(ciboSourcedValue)}
          variant="text"
          color="secondary"
          sx={{ paddingBlock: 0.25 }}
        >
          {t('useEmail', { email: ciboSourcedValue })}
        </Button>
      </Collapse>

      {!!helperText && (
        <Markdown typographyProps={{ variant: 'caption' }}>
          {engagement.data?.replaceCopyTokens(helperText)}
        </Markdown>
      )}
    </Stack>
  )
}
